import { Clipboard } from '@angular/cdk/clipboard';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { PendoService } from 'analytics-lib';
import { IDataSourcePathCommand } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { forkJoin, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BullhornDataService } from '../../../services/bullhorn-data.service';
import { LinkedEntitiesService } from '../../../services/linked-entities.service';
import { NotetakerCallsService } from '../../../services/notetaker-calls.service';
import { SaveNoteService } from '../../../services/saveNote.service';
import { IBullhornCardContext } from '../../../types/bullhorn-card-context';
import { BullhornEntityType } from '../../../types/bullhorn/bullhorn-entity-type';
import { IBullhornPostResponse } from '../../../types/bullhorn/bullhorn-result';
import { IHighlight, IQandA } from '../../../types/bullhorn/ICallSummary';
import { INote } from '../../../types/bullhorn/note';
import { ICall } from './ICallInterface';

@Component({
  selector: 'app-notetaker-result',
  templateUrl: './notetaker-result.component.html',
  styleUrls: ['./notetaker-result.component.scss'],
  standalone: false,
})
export class NotetakerResultComponent implements OnInit {
  @Input() call!: ICall;
  @Input() bullhornCardContext!: IBullhornCardContext;
  @Output() clearSelectedCall = new EventEmitter<ICall | null>();

  @ViewChild('clearSearch') clearSearch!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('input') inputElement: ElementRef | undefined;
  @ViewChild('textarea') textareaElement: ElementRef | undefined;

  editingValue: { [key: string]: boolean } = {};
  lastBlurTime = 0;
  inputOrTextareaCharLimit = 80;
  zeroCallDuration = '0m 0s';
  transcriptWithNames = true;

  private readonly DEFAULT_NOTE_ACTION = 'Kyloe AI NoteTaker Summary';

  private lastClickTime = 0;
  private doubleClickThreshold = 300;
  private isDoubleClick = false;

  activeTab = 'Questions';
  searchTerm = '';
  private initialSummaryValue: string | undefined = '';
  private initialQandAs: IQandA[] = [];
  private initialHighlights: IHighlight[] = [];
  private initialNote = '';

  summaryValue: string | undefined = 'Loading...';
  summaryEdited = false;
  questionsEdited: { [key: string]: boolean } = {};

  protected QandAs: IQandA[] = [];
  protected notFoundString = 'Not found';

  protected isLoading = true;

  hasDifferences = false;
  questionMatches: { [key: string]: boolean } = {};
 public showLinkedEntitiesPopup = false;
 public linkedEntitiesCount = 0;

  constructor(
    private messageNotificationService: MessageNotificationService,
    private bullhornDataService: BullhornDataService,
    private clipboard: Clipboard,
    private noteService: SaveNoteService,
    private dialog: MatDialog,
    private linkedEntitiesService: LinkedEntitiesService,
    private notetakerCallsService: NotetakerCallsService,
    private pendoService: PendoService,
  ) {}

  ngOnInit(): void {
    if (this.call.summary) {
      const summaryItem = this.call.summary.summary.highlights.find(
        item => item.title.toLowerCase() === 'summary'
      );
      this.processSummary(summaryItem);
    } else {
      this.processSummary(undefined);
      this.waitForSummaryLoad();
    }
  }

  private waitForSummaryLoad(): void {
    const checkSummary = () => {
      if (this.call.summary) {
        const summaryItem = this.call.summary.summary.highlights.find(
          item => item.title.toLowerCase() === 'summary'
        );
        this.processSummary(summaryItem);
      } else {
        setTimeout(checkSummary, 100);
      }
    };
    checkSummary();
  }

  private processSummary(summaryItem: IHighlight | undefined) {
    if (summaryItem) {
      this.summaryValue = summaryItem.value;
    }

    this.initialSummaryValue = this.summaryValue;
    this.initialQandAs = JSON.parse(JSON.stringify(this.call.summary?.QandAs || []));
    this.initialHighlights = JSON.parse(JSON.stringify(this.call.summary?.summary?.highlights || []));
    this.initialNote = this.call.summary?.notes || '';

    this.call.summary?.QandAs.forEach((q) => {
      this.questionsEdited[q.question.id] = false;
    });
    this.fetchAnswers();
    this.getBullhornMetaLables();
    this.loadLinkedEntitiesCount();
    this.isLoading = false;
  }

  fetchAnswers() {
    const numberOfFields: string[] = [];

    this.call.summary?.QandAs.forEach((qa) => {
      const question =
        qa.question?.destination
          ?.split('.')
          .pop()
          ?.replace(/\[\d+]/g, '')
          .trim() || '';

      if (question && !numberOfFields.includes(question)) {
        numberOfFields.push(question);
      }
    });

    if (numberOfFields.length > 0) {
      this.bullhornDataService
        .getEntityById(
          this.bullhornCardContext.cardEntityType,
          this.bullhornCardContext.cardEntityId,
          numberOfFields
        )
        .subscribe({
          next: (result: Record<string, any>) => {
            this.hasDifferences = false;

            this.call.summary?.QandAs.forEach((qa, index) => {
              const destination = qa.question?.destination || '';
              const fieldKey = destination
                .split('.')
                .pop()
                ?.replace(/\[\d+]/g, '')
                .trim();

              if (fieldKey && result[fieldKey] !== undefined) {
                let qaAnswerString = String(qa.answer);
                let resultValueString = String(result[fieldKey]);

                if (fieldKey === 'dateAvailable') {
                  const apiDate = new Date(Number(result[fieldKey]));
                  resultValueString = apiDate.toLocaleDateString('en-US');

                  const qaDate = new Date(qa.answer);
                  if (!isNaN(qaDate.getTime())) {
                    qaAnswerString = qaDate.toLocaleDateString('en-US');
                  }
                }

                if (qaAnswerString !== resultValueString) {
                  this.hasDifferences = true;
                  this.questionMatches[qa.question.id] = false;
                } else {
                  this.questionMatches[qa.question.id] = true;
                }
              }
            });
          },
          error: (error) => {
            console.error('Error fetching answers:', error);
          },
        });
    } else {
      console.warn('No questions to process.');
    }
  }

  // Individual QandA save
  onSave(QandA: any): void {
    const previousAnswer = this.initialQandAs.find(q => q.question.id === QandA.question.id)?.answer;
    this.QandAPendoTrackEvent(QandA, false, previousAnswer);

    // Save the current answer
    this.saveSingleAnswer(QandA);

    // After saving, disable the button for this specific question
    this.questionMatches[QandA.question.id] = true;
  }

  private QandAPendoTrackEvent(QandA: any, isBulkSave: boolean = false, previousAnswer?: any) {
    const prevAnswer = previousAnswer !== undefined
      ? previousAnswer
      : this.initialQandAs.find(q => q.question.id === QandA.question.id)?.answer;

    const pendoEventData = {
      questionId: QandA.question.id,
      questionField: this.pendoService.truncateForPendo(QandA.question.question),
      previousValue: this.pendoService.truncateForPendo(prevAnswer),
      newValue: this.pendoService.truncateForPendo(QandA.answer),
      fieldModified: this.pendoService.truncateForPendo(QandA.question.destination),
      entityId: this.bullhornCardContext.cardEntityId,
      entityType: this.bullhornCardContext.cardEntityType,
      isBulkSave: isBulkSave
    };
    this.pendoService?.recordEvent('NT Card | Answer Saved', pendoEventData);
  }

  private trackSaveAllChanges(changedQandAs: { qa: any; previousAnswer: any }[], entityId: number, entityType: string) {
    const fieldsModified = changedQandAs
      .map(({ qa }) => qa.question.destination || qa.question.question)
      .join(', ');

    const pendoEventData = {
      entityId: entityId,
      entityType: entityType,
      changesCount: changedQandAs.length,
      fieldsModified: this.pendoService.truncateForPendo(fieldsModified, 300)
    };
    this.pendoService?.recordEvent('NT Card | Save All Q and A Changes Button', pendoEventData);
  }

  getBullhornMetaLables() {
    const destinations = this.collectUniqueDestinations();
    if (destinations.length > 0) {
      this.fetchAndProcessFieldLabels(destinations);
    } else {
      console.log('No destinations to fetch labels for.');
    }
  }
  get notes(): string {
    return this.call.summary?.notes || '';
  }

  set notes(value: string) {
    if (this.call && this.call.summary) {
      this.call.summary.notes = value;
    }
  }
  private collectUniqueDestinations(): string[] {
    const postData: string[] = [];
    this.call.summary?.QandAs.forEach((qaObj: any) => {
      const destination = this.trimValue(qaObj.question?.destination);
      if (destination && destination !== '' && !postData.includes(destination)) {
        postData.push(destination);
      }
    });
    return postData;
  }

  private fetchAndProcessFieldLabels(destinations: string[]): void {
    this.bullhornDataService
      .getEntityMetaFullFromStorage(this.bullhornCardContext.cardEntityType, destinations)
      .then((entityMeta) => {
        const fields = entityMeta.fields;
        if (Array.isArray(fields) && fields.length > 0) {
          this.matchAndProcessFields(fields);
        } else {
          console.log('No fields available or result is not an array.');
        }
      });
  }

  private matchAndProcessFields(fields: any[]): void {
    const qaMap = this.buildQAMap();

    fields.forEach((field: any) => {
      const matchingQA = qaMap.get(field.name);
      if (matchingQA) {
        matchingQA.fieldLabel = field.label;
      } else {
        console.log(`No match found for ${field.label}`);
      }
    });
  }

  private buildQAMap(): Map<string, any> {
    const qaMap = new Map();
    this.call.summary?.QandAs.forEach((qaObj: any) => {
      const destination = this.trimValue(qaObj.question?.destination);
      if (destination && destination !== '') {
        qaMap.set(destination, qaObj);
      }
    });
    return qaMap;
  }

  trimValue(value: string): string {
    if (value && value.includes('.')) {
      const parts = value.split('.');
      return parts[parts.length - 1];
    } else {
      return '';
    }
  }

  private formatPutData(putData: INote): string {
    return JSON.stringify({
      comments: putData.comments,
      personReference: {
        id: this.bullhornCardContext.cardEntityId,
      },
      action: this.call.noteAction ?? this.DEFAULT_NOTE_ACTION,
      isDeleted: putData.isDeleted
    });
  }

  openPopup() {
    this.showLinkedEntitiesPopup = true;
  }

  onTogglePopup(show: boolean) {
    this.showLinkedEntitiesPopup = show;
    if (!show) {
      this.loadLinkedEntitiesCount();
    }
  }

  private loadLinkedEntitiesCount(): void {
    if (this.bullhornCardContext && this.call?.callId) {
      this.linkedEntitiesService
        .getLinkedEntitiesForCall(this.bullhornCardContext.corporationId, this.call.callId)
        .subscribe({
          next: (entities) => {
            this.linkedEntitiesCount = entities.length;
          },
          error: (error) => {
            console.error('Error loading linked entities count:', error);
            this.linkedEntitiesCount = 0;
          },
        });
    }
  }

  protected saveCallSummaryAsNote(): void {
    const displayNoteId = this.call.displaySummaryPath?.path?.[0] === BullhornEntityType.NOTE ? this.call.displaySummaryPath.path[1] : undefined;
    const putData = this.formatPutData({ comments: this.generateFormattedContent(), isDeleted: false } as INote);

    this.noteService.saveNote(putData, 'Saved as note', 'Failed to save note', displayNoteId).subscribe({
      next: (noteId) => {
        if (noteId && !displayNoteId) this.updateCallWithDisplaySummaryPath(noteId);
      }
    });
  }

  generateFormattedContent(): string {
    let content = `AI Generated Summary of the Call\n\n`;
    content += this.generateSummarySection();
    content += this.generateHighlightsSection();
    content += this.generateQuestionsAndAnswersSection();
    content += this.generateNotesSection();
    return content;
  }

  private generateSummarySection(): string {
    return `Summary:\n${this.summaryValue || 'No summary available.'}\n\n`;
  }

  private generateHighlightsSection(): string {
    let highlightsContent = `Highlights:\n`;
    if (this.call.summary?.summary?.highlights?.length) {
      this.call.summary.summary.highlights.forEach((highlight) => {
        highlightsContent += `- ${highlight.title}: ${highlight.value}\n`;
      });
    } else {
      highlightsContent += 'No highlights available.\n';
    }
    highlightsContent += `\n`;
    return highlightsContent;
  }

  private generateQuestionsAndAnswersSection(): string {
    let qaContent = `Questions and Answers:\n`;
    this.call.summary?.QandAs.forEach((qAndA) => {
      qaContent += `Question: ${qAndA.question?.question || 'N/A'}\n`;
      qaContent += `Answer: ${qAndA.answer || 'N/A'}\n\n`;
    });
    return qaContent;
  }

  private generateNotesSection(): string {
    return `Notes:\n${this.call.summary?.notes || 'No notes available.'}\n\n`;
  }

  saveSummaryButtonClick(event: Event): void {
    event.stopPropagation();
    const { highlightChanges, currentNotes, notesChanged, summaryChanged } = this.buildSummaryPendoTrackEvent();

    const saveSummaryObs$ = this.saveSummary();
    if (saveSummaryObs$) {
      saveSummaryObs$.subscribe({
        next: () => {
          this.saveCallSummaryAsNote();
          this.summaryPendoTrackEvent(summaryChanged, notesChanged, currentNotes, highlightChanges);
        },
        error: (error) => console.log(`Error updating call summary`, error)
      });
    }
  }

  private buildSummaryPendoTrackEvent() {
    const allComparisons = this.call.summary?.summary?.highlights?.map((highlight) => {
      const previousHighlight = this.initialHighlights.find(h => h.title === highlight.title);
      return {
        highlightTitle: this.pendoService.truncateForPendo(highlight.title),
        previousValue: this.pendoService.truncateForPendo(previousHighlight?.value),
        newValue: this.pendoService.truncateForPendo(highlight.value),
        hasChanged: previousHighlight?.value !== highlight.value
      };
    }) || [];

    const highlightChanges = allComparisons
      .filter(change => change.hasChanged)
      .map(({ hasChanged, ...rest }) => rest);

    const currentNotes = this.call.summary?.notes || '';
    const notesChanged = currentNotes !== this.initialNote;
    const summaryChanged = this.summaryValue !== this.initialSummaryValue;
    return { highlightChanges, currentNotes, notesChanged, summaryChanged };
  }

  private summaryPendoTrackEvent(summaryChanged: boolean, notesChanged: boolean, currentNotes: string, highlightChanges: {
    highlightTitle: string;
    previousValue: string;
    newValue: string
  }[]) {
    highlightChanges.forEach(highlight => {
      const pendoEventData = {
        entityId: this.bullhornCardContext.cardEntityId,
        entityType: this.bullhornCardContext.cardEntityType,
        fieldType: 'highlight',
        fieldName: highlight.highlightTitle,
        previousValue: highlight.previousValue,
        newValue: highlight.newValue,
        isBulkSave: true
      };
      this.pendoService?.recordEvent('NT Card | Summary Highlight Saved', pendoEventData);
    });

    if (summaryChanged) {
      const pendoEventData = {
        entityId: this.bullhornCardContext.cardEntityId,
        entityType: this.bullhornCardContext.cardEntityType,
        fieldType: 'summary',
        previousValue: this.pendoService.truncateForPendo(this.initialSummaryValue),
        newValue: this.pendoService.truncateForPendo(this.summaryValue),
        isBulkSave: true
      };
      this.pendoService?.recordEvent('NT Card | Summary Saved', pendoEventData);
    }

    if (notesChanged) {
      const pendoEventData = {
        entityId: this.bullhornCardContext.cardEntityId,
        entityType: this.bullhornCardContext.cardEntityType,
        fieldType: 'notes',
        previousValue: this.pendoService.truncateForPendo(this.initialNote),
        newValue: this.pendoService.truncateForPendo(currentNotes),
        isBulkSave: true
      };
      this.pendoService?.recordEvent('NT Card | Note Saved', pendoEventData);
    }

    this.trackSaveSummaryButton(highlightChanges, summaryChanged, notesChanged);
  }

  private trackSaveSummaryButton(
    highlightChanges: { highlightTitle: string; previousValue: string; newValue: string }[],
    summaryChanged: boolean,
    notesChanged: boolean
  ) {
    const fieldsModified: string[] = [];

    if (summaryChanged) {
      fieldsModified.push('summary');
    }

    highlightChanges.forEach(h => fieldsModified.push(h.highlightTitle));

    if (notesChanged) {
      fieldsModified.push('notes');
    }

    const totalChanges = highlightChanges.length + (summaryChanged ? 1 : 0) + (notesChanged ? 1 : 0);

    const pendoEventData = {
      entityId: this.bullhornCardContext.cardEntityId,
      entityType: this.bullhornCardContext.cardEntityType,
      changesCount: totalChanges,
      fieldsModified: this.pendoService.truncateForPendo(fieldsModified.join(', '), 300)
    };
    this.pendoService?.recordEvent('NT Card | Save Summary Button', pendoEventData);
  }

  checkQuestionChange(questionId: string, newAnswer: string): void {
    const initialAnswer = this.initialQandAs.find((q) => q.question.id === questionId)?.answer;
    this.questionMatches[questionId] = newAnswer === initialAnswer;

    this.updateUnsavedDifferencesState();
  }

  filteredTranscript() {
    if (!this.call.transcript) {
      return [];
    }

    if (!this.searchTerm) {
      this.transcriptWithNames = this.call.transcript.some(it => it.name !== null);
      return this.call.transcript;
    }
    const transcript = this.call.transcript?.filter((item) =>
      item.text.toLowerCase().includes(this.searchTerm.toLowerCase())
    );

    this.transcriptWithNames = transcript.some(it => it.name !== null);
    return transcript;
  }

  // TODO check if this is needed
  // SAVE ALL CHANGES BUTTON
  saveResult(): void {
    const changedQandAsWithPreviousValues = this.previousQandAValues();

    this.buildPostData().then((postData) => {
      const entityType = this.bullhornCardContext.cardEntityType;
      const entityId = this.bullhornCardContext.cardEntityId as number;
      const obs$ = [];
      obs$.push(this.updateBHEntity(entityType, entityId, postData));
      const saveSummaryObs$ = this.saveSummary();
      if (saveSummaryObs$) {
        obs$.push(saveSummaryObs$);
      }
      forkJoin(obs$).subscribe({
        next: () => {
          console.log('Saving to fields: ' + JSON.stringify(postData));
          this.messageNotificationService.success(`Saved to fields on ${entityType}`);

          // Send individual Pendo events for each changed field
          changedQandAsWithPreviousValues.forEach(({ qa, previousAnswer }) => {
            this.QandAPendoTrackEvent(qa, true, previousAnswer);
          });

          // Send summary event for Save All Changes button
          this.trackSaveAllChanges(changedQandAsWithPreviousValues, entityId, entityType);

          this.hasDifferences = false;
          this.call.summary?.QandAs.forEach((qa) => {
            this.questionMatches[qa.question.id] = true;
          });
          this.saveCallSummaryAsNote();
        },
        error: (error) => {
          console.log(`Error saving fields to`, error);
          this.messageNotificationService.error('Failed to save to Bullhorn');
        },
      });
    });
  }

  private previousQandAValues() {
    return this.call.summary?.QandAs.filter((qa) => {
      const previousAnswer = this.initialQandAs.find(q => q.question.id === qa.question.id)?.answer;
      return previousAnswer !== qa.answer;
    }).map((qa) => {
      const previousAnswer = this.initialQandAs.find(q => q.question.id === qa.question.id)?.answer;
      return {
        qa: qa,
        previousAnswer: previousAnswer
      };
    }) || [];
  }

  private async buildPostData(QandA?: any): Promise<{ [key: string]: string }> {
    const postData: { [key: string]: string } = {};
    const QandAs = QandA ? [QandA] : this.call.summary?.QandAs || [];

    for (const item of QandAs) {
      if (item.question.destination && item.question.destination !== '') {
        const destination = item.question.destination.split('.');
        if (
          destination[0] === 'bullhorn' &&
          destination[1] === this.bullhornCardContext.cardEntityType
        ) {
          if (item.answer != null && item.answer !== this.notFoundString) {
            postData[destination[2]] = await this.bullhornDataService.truncateField(
              this.bullhornCardContext.cardEntityType,
              destination[2],
              item.answer
            );
          }
        }
      }
    }
    return postData;
  }

  private updateBHEntity(
    entityType: string,
    entityId: number,
    postData: any
  ): Observable<IBullhornPostResponse> {
    return this.bullhornDataService.updateEntity(entityType, entityId, postData).pipe(
      tap((result) => {
        console.log('result', result);
      })
    );
  }

  private saveSummary(): Observable<void> | null {
    this.call.summary?.QandAs.forEach((qa) => {
      if (qa.question?.destination) {
        const destination = qa.question.destination
          .split('.')
          .pop()
          ?.replace(/\[\d+]/g, '')
          .trim();
        if (destination && qa.answer !== undefined) {
          const matchingQA = this.initialQandAs.find(
            (initialQA) => initialQA.question.id === qa.question.id
          );
          if (matchingQA) {
            matchingQA.answer = qa.answer;
          }
        }
      }
    });

    const summaryItem = this.call.summary?.summary.highlights.find(
      (item) => item.title.toLowerCase() === 'summary'
    );
    if (summaryItem) {
      summaryItem.value = this.summaryValue ?? '';
    }

    const jsonString = JSON.stringify(this.call.summary);

    if (this.call.summaryPath?.path) {
      return of(this.saveSummaryToFile(this.call.summaryPath, jsonString));
    } else {
      return null;
    }
  }

  hasUnsavedSummaryChanges(): boolean {
    const isSummaryEdited = this.summaryEdited;

    const areHighlightsEdited = this.call.summary?.summary?.highlights
      ? this.call.summary.summary.highlights.some(
          (highlight) => this.questionsEdited[highlight.title] === true
        )
      : false;

    return isSummaryEdited || areHighlightsEdited;
  }

  private saveSummaryToFile(summaryFile: IDataSourcePathCommand, summary: string, resetInitialValues: boolean = true) {
    let noteId: string | undefined = undefined;
    if ((summaryFile.path) && (summaryFile.path[0] === BullhornEntityType.NOTE)) {
      noteId = summaryFile.path[1];
    }

    const putData: string = this.formatPutData({
      comments: summary,
    } as INote);

    this.noteService.saveNote(putData, '', '', noteId).subscribe();

    if (resetInitialValues) {
      this.initialSummaryValue = this.summaryValue;
      this.summaryEdited = false;
      // todo  check this
      this.call.summary?.QandAs.forEach((qa) => {
        this.questionsEdited[qa.question.id] = false;
      });
      this.initialQandAs = JSON.parse(JSON.stringify(this.call.summary?.QandAs));
    }
  }

  private updateUnsavedDifferencesState(): void {
    this.hasDifferences = Object.values(this.questionMatches).some((match) => !match);
  }

  saveSingleAnswer(QandA: any): void {
    this.buildPostData(QandA).then((postData) => {
      const entityType = this.bullhornCardContext.cardEntityType;
      const entityId = this.bullhornCardContext.cardEntityId as number;

      this.bullhornDataService.updateEntity(entityType, entityId, postData).subscribe({
        next: (result) => {
          console.log('Saved answer:', result);
          this.messageNotificationService.success(
            `Saved to ${Object.keys(postData).join(', ')} on ${entityType}`
          );
          this.questionsEdited[QandA.question.id] = false;
          const questionToUpdate = this.initialQandAs.find(
            (q) => q.question.id === QandA.question.id
          );
          if (questionToUpdate) {
            questionToUpdate.answer = QandA.answer;
          }
          this.updateUnsavedDifferencesState();
          this.updateSummaryFile(this.call.summaryPath!, JSON.stringify(this.call.summary), false);
          // todo test
          this.saveCallSummaryAsNote();
        },
        error: (error) => {
          console.log(`Error saving single answer: `, error.toString());
          this.messageNotificationService.error(`Failed to save the answer for ${Object.keys(postData).join(', ')}. Please try again.`);
        },
      });
    });
  }

  private updateSummaryFile(summaryFile: IDataSourcePathCommand, summary: string, resetInitialValues: boolean = true) {
    this.saveSummaryToFile(summaryFile, summary, resetInitialValues);
  }

  private updateCallWithDisplaySummaryPath(noteId: string): void {
    this.call.displaySummaryPath = { namespace: 'bullhorn', path: ['Note', noteId] } as IDataSourcePathCommand;
    this.notetakerCallsService.saveCall(this.call).subscribe();
  }

  protected backToCallsList(): void {
    this.clearSelectedCall.emit(null);
  }

  protected handleSearchBoxPanel(panel: MatExpansionPanel, event: MouseEvent): void {
    if (panel._getExpandedState() === 'expanded') event.stopPropagation();
  }

  protected blurred(jsonIdentifier: string): void {
    this.lastBlurTime = new Date().getTime();
    this.editingValue[jsonIdentifier] = false;

    this.summaryEdited = this.summaryValue !== this.initialSummaryValue;
    if (jsonIdentifier === 'Summary') {
      this.summaryEdited = this.summaryValue !== this.initialSummaryValue;
    }
    const highlight = this.call.summary?.summary?.highlights.find(
      (h) => h.title === jsonIdentifier
    );
    if (highlight) {
      this.questionsEdited[jsonIdentifier] =
        highlight.value !==
        this.initialQandAs.find((q) => q.question.id === highlight.title)?.answer;
    }
  }

  protected editValue(aspect: string): void {
    this.editingValue[aspect] = !this.editingValue[aspect];
    setTimeout(() => {
      this.inputElement?.nativeElement.focus();
      this.textareaElement?.nativeElement.focus();
    }, 0);
  }

  protected copyToClipboard(aString: string): void {
    this.clipboard.copy(aString);
    this.messageNotificationService.success(`Copied ${aString} to clipboard`);
  }
  get isSummaryDisabled(): boolean {
    return (
      this.isLoading ||
      !this.call.summaryPath ||
      !this.call.summaryPath.namespace ||
      this.call.summaryPath.namespace === ''
    );
  }

  protected selectOrEdit(aspect: string, event: MouseEvent, aspectToCopy?: string): void {
    // Left clicks only
    if (event.button === 0) {
      const currentTime = new Date().getTime();
      const timeSinceLastClick = currentTime - this.lastClickTime;

      // Double clicks
      if (timeSinceLastClick < this.doubleClickThreshold) {
        this.isDoubleClick = true;
        this.copyToClipboard(aspectToCopy || aspect);
        return;
      }

      this.lastClickTime = currentTime;

      // Single clicks
      setTimeout(() => {
        if (!this.isDoubleClick) {
          const selection = window.getSelection();
          if (selection?.toString().length === 0) this.editValueIfNotJustBlurred(aspect);
        }
        this.isDoubleClick = false;
      }, this.doubleClickThreshold);
    }
  }

  // This is nasty and to do with timing -
  // The blur has just shown the text again, but the text is catching clicks again immediately
  protected editValueIfNotJustBlurred(aspect: string): void {
    const currentTime = new Date().getTime();
    const timeSinceLastBlur = currentTime - this.lastBlurTime;
    if (timeSinceLastBlur > 700) {
      // The text picks up the click immediately even though the blur only just showed it again
      this.editValue(aspect);
    }
  }

  protected clearInput(inputElement: HTMLElement, focusInput?: boolean, event?: MouseEvent): void {
    event?.stopPropagation(); // TODO: Using the keyboard to clear collapses the panel
    this.searchTerm = '';
    if (focusInput) inputElement.focus();
  }

  // Keyboard Shortcuts
  // - Use Escape to clear Search
  // - Alt + Left to go back to Calls List
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    const clearSearch = this.clearSearch?.nativeElement;
    if (event.code === 'Escape' && this.clearSearch) clearSearch.click();
    if (event.ctrlKey && event.altKey && event.key === 'ArrowLeft') {
      this.backToCallsList();
    }
  }
}
